import { useId, useState } from 'react';
import {
    type Control,
    Controller,
    type UseFormSetValue,
    useFormState,
    useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Alert from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import SecretInput from '@/components/atoms/SecretInput';
import type { ProviderGroup, ProviderRow } from '@/domain/providerConfig';
import { errorKeyOf, fieldLabelSuffix } from '@/routes/shared/ProviderConfig/ProviderSchemaField';

import type { ProviderFormValues } from '../form/formModel';

import { knownOutlook, needsAttention, type PenSecretField, penSecretState } from './sectionFields';

import styles from './sections.module.scss';

export interface PenCredentialsProps {
    base: `pens.${string}`;
    row: ProviderRow | null;
    group: ProviderGroup | null;
    secrets: PenSecretField[];
    control: Control<ProviderFormValues>;
    setValue: UseFormSetValue<ProviderFormValues>;
}

/**
 * Whether the pen holds what the site holds: a stored copy while every pen stores the same
 * bytes, or the site's secret that a new pen takes on save. A second set of boxes for the
 * same credentials then says nothing the site's boxes do not.
 */
function followsSite(
    secret: PenSecretField,
    row: ProviderRow | null,
    group: ProviderGroup | null
): boolean {
    const outlook = knownOutlook(row, secret.name, secret.outlook);
    if (outlook === 'inherited') return true;
    const sameEverywhere = group?.credentials[secret.name]?.state === 'set';
    return sameEverywhere && (outlook === 'stored' || outlook === 'clearedWithSite');
}

/** A pen's own credentials: one line while it uses the site's, its own boxes otherwise. */
function PenCredentials({ base, row, group, secrets, control, setValue }: PenCredentialsProps) {
    const { t } = useTranslation();
    // React's ids hold colons, which break the `#id` selectors jsdom and some libraries build.
    const id = useId().replaceAll(':', '');
    const [ownChosen, setOwnChosen] = useState(false);
    const values = useWatch({ control, name: `${base}.secrets` });
    const paths = secrets.map(({ name }) => `${base}.secrets.${name}`);
    const { errors } = useFormState({ control, name: base });
    if (secrets.length === 0) return null;

    const canFollow = secrets.every((secret) => followsSite(secret, row, group));
    const untouched = secrets.every(({ name }) => (values?.[name]?.mode ?? 'keep') === 'keep');
    const cleared = secrets.filter(
        (secret) => knownOutlook(row, secret.name, secret.outlook) === 'clearedWithSite'
    ).length;
    // One line can say "uses the site's" or "cleared with the site's", not both, so a pen a
    // site Clear takes only part of shows each box with its own state.
    const oneState = cleared === 0 || cleared === secrets.length;
    // A message on one of the boxes has to be seen, so they stay open while there is one.
    const collapsed =
        canFollow && oneState && untouched && !ownChosen && !needsAttention(errors, [], paths);

    const useOwn = (
        <Button variant="secondary" wrap onClick={() => setOwnChosen(true)}>
            {t('providerConfig.pens.useOwnCredentials')}
        </Button>
    );

    function followSiteAgain(): void {
        for (const { name } of secrets)
            setValue(`${base}.secrets.${name}`, { mode: 'keep' }, { shouldDirty: true });
        setOwnChosen(false);
    }

    return (
        <fieldset className={styles.group}>
            <legend className={styles.subheading}>{t('providerConfig.pens.credentials')}</legend>
            {collapsed && cleared > 0 && (
                // Follows the site's Clear on this card, which the boxes there already show.
                <Alert tone="warning" size="small" live="off" action={useOwn}>
                    {t('providerConfig.pens.clearedWithSiteCredentials')}
                </Alert>
            )}
            {collapsed && cleared === 0 && (
                <div className={styles.penCredentialsLine}>
                    <span className={styles.note}>
                        {t('providerConfig.pens.usesSiteCredentials')}
                    </span>
                    {useOwn}
                </div>
            )}
            {!collapsed && (
                <div className={styles.fields}>
                    {secrets.map(({ name, labelKey, outlook }) => (
                        <Controller
                            key={name}
                            control={control}
                            name={`${base}.secrets.${name}`}
                            render={({ field, fieldState }) => (
                                <FormField
                                    label={t(labelKey)}
                                    // Kept free of "password", which password managers fill.
                                    htmlFor={`${id}-${name}`}
                                    // A pen's own box overrides the site's, so it is never needed.
                                    labelSuffix={fieldLabelSuffix({ optional: true }, t)}
                                    error={
                                        fieldState.error
                                            ? t(errorKeyOf(fieldState.error.message))
                                            : null
                                    }>
                                    <SecretInput
                                        {...penSecretState(row, name, outlook)}
                                        changedAt={null}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                </FormField>
                            )}
                        />
                    ))}
                    {canFollow && (
                        <Button
                            variant="secondary"
                            wrap
                            className={styles.inlineAction}
                            onClick={followSiteAgain}>
                            {t('providerConfig.pens.useSiteCredentials')}
                        </Button>
                    )}
                </div>
            )}
        </fieldset>
    );
}

export default PenCredentials;
export { PenCredentials };
